import { NODE_TYPE } from '../../types/node';

import { conditionConfig } from './condition.config';
import { conditionalStepConfig } from './conditional-step.config';
import { emailTriggerConfig } from './email-trigger.config';
import { forLoopConfig } from './for-loop.config';
import { messageTriggerConfig } from './message-trigger.config';
import { nestedWorkflowConfig } from './nested-workflow.config';
import { scheduledTriggerConfig } from './scheduled-trigger.config';
import { specializedAgentConfig } from './specialized-agent.config';
import { webhookTriggerConfig } from './webhook-trigger.config';

/**
 * Central registry of all workflow node configurations.
 * Use `as const` for full type inference and autocomplete.
 *
 * This object replaces the class-based NodeRegistry pattern with a
 * declarative, tree-shakeable approach.
 *
 * @example
 * ```tsx
 * import { nodeConfigs } from './nodes/configs';
 *
 * <WorkflowProvider nodeConfigs={nodeConfigs}>
 *   <WorkflowBuilder />
 * </WorkflowProvider>
 * ```
 */
export const nodeConfigs = {
  [NODE_TYPE.SPECIALIZED]: specializedAgentConfig,
  [NODE_TYPE.EMAIL]: emailTriggerConfig,
  [NODE_TYPE.WEBHOOK]: webhookTriggerConfig,
  [NODE_TYPE.MESSAGE]: messageTriggerConfig,
  [NODE_TYPE.SCHEDULED]: scheduledTriggerConfig,
  [NODE_TYPE.CONDITION]: conditionConfig,
  [NODE_TYPE.CONDITIONAL]: conditionalStepConfig,
  [NODE_TYPE.FOR_LOOP]: forLoopConfig,
  [NODE_TYPE.NESTED_WORKFLOW]: nestedWorkflowConfig,
} as const;

/**
 * Type helper to extract node type keys from the configs object.
 * Provides compile-time validation for node type strings.
 */
export type NodeConfigKey = keyof typeof nodeConfigs;

/**
 * Export individual configs for direct access if needed.
 */
export {
  conditionalStepConfig,
  conditionConfig,
  emailTriggerConfig,
  forLoopConfig,
  messageTriggerConfig,
  nestedWorkflowConfig,
  scheduledTriggerConfig,
  specializedAgentConfig,
  webhookTriggerConfig,
};
